import * as yup from "yup";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Col } from "../UI/Layout/Col.tsx";
import { Row } from "../UI/Layout/Row.tsx";
import { InputWithLabel } from "../UI/InputWithLabel.tsx";
import { TextInput } from "../UI/TextInput.tsx";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { useWithLoading } from "../UI/Loading/useWithLoading.ts";
import { UserGroupAvailabilityInput } from "./useCreateUserGroup.ts";
import { WeekDayEnum } from "./WeekDayEnum.tsx";
import {
  AvailabilityFormItem,
  WeeklyAvailabilityCard,
} from "./WeeklyAvailabilityCard.tsx";
import {
  formatHour,
  WEEK_DAYS_IN_ORDER,
  weekDayLabels,
} from "./weeklyAvailabilityFormat.ts";

export type UserGroupFormInputs = {
  name: string;
  thinkificBundleId?: number;
  availability?: UserGroupAvailabilityInput[];
  onboardingLiveRequired: boolean;
  onboardingLiveBookingWindowDays: number;
};

type FormValues = Omit<UserGroupFormInputs, "availability"> & {
  availability?: AvailabilityFormItem[];
};

type Props = {
  defaultValues?: Partial<FormValues>;
  onSave: (data: UserGroupFormInputs) => Promise<void>;
  isEditing?: boolean;
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const schema = yup
  .object({
    name: yup.string().required("Specifica un nome gruppo"),
    thinkificBundleId: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" || Number.isNaN(value) ? undefined : value,
      )
      .integer("Deve essere un numero intero")
      .min(1, "Deve essere maggiore di 0")
      .optional(),
    availability: yup
      .array()
      .of(
        yup.object({
          weekDay: yup
            .string()
            .oneOf(Object.values(WeekDayEnum), "Giorno non valido")
            .required("Giorno richiesto"),
          timeIntervals: yup
            .array()
            .of(
              yup
                .object({
                  from: yup
                    .number()
                    .typeError("Obbligatorio")
                    .integer("Solo ore intere")
                    .min(0, "Min 0")
                    .max(23, "Max 23")
                    .required("Obbligatorio"),
                  to: yup
                    .number()
                    .typeError("Obbligatorio")
                    .integer("Solo ore intere")
                    .min(0, "Min 0")
                    .max(23, "Max 23")
                    .required("Obbligatorio"),
                })
                .test(
                  "from-before-to",
                  "L'ora di fine deve essere successiva all'ora di inizio",
                  (val) => {
                    if (!val) return false;
                    const from = val.from;
                    const to = val.to;
                    return to > from;
                  },
                ),
            )
            .min(1, "Aggiungi almeno un intervallo")
            .required("Obbligatorio"),
        }),
      )
      .optional(),
    onboardingLiveRequired: yup.boolean().required(),
    onboardingLiveBookingWindowDays: yup
      .number()
      .typeError("Inserisci un numero di giorni")
      .integer("Deve essere un numero intero")
      .min(1, "Almeno 1 giorno")
      .required("Inserisci un numero di giorni"),
  })
  .required();

function FieldErrorMessage({ message }: { message?: string }) {
  return message ? (
    <p className="mt-1 text-xs text-red-600">{message}</p>
  ) : null;
}

export function UserGroupForm({ defaultValues, onSave, isEditing }: Props) {
  const {
    handleSubmit,
    register,
    control,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      onboardingLiveRequired: false,
      onboardingLiveBookingWindowDays: 14,
      ...(defaultValues ?? {}),
    },
  });
  const onboardingLiveRequired = watch("onboardingLiveRequired");

  const availabilityFieldArray = useFieldArray({
    control,
    name: "availability",
  });

  const availability = watch("availability") || [];

  const { withLoading, loading } = useWithLoading();
  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const availabilityOut: UserGroupAvailabilityInput[] | undefined =
      data.availability?.map((a) => ({
        weekDay: a.weekDay,
        timeIntervals: a.timeIntervals.map((ti) => ({
          from: ti.from,
          to: ti.to,
        })),
        timeIntervalTimezone: "Europe/Rome",
      }));

    await withLoading(async () =>
      onSave({
        name: data.name,
        thinkificBundleId: data.thinkificBundleId,
        availability: availabilityOut,
        onboardingLiveRequired: data.onboardingLiveRequired,
        onboardingLiveBookingWindowDays: data.onboardingLiveBookingWindowDays,
      }),
    );
  };

  const configuredWeekDays = availability.map((a) => a.weekDay);
  const firstFreeWeekDay = WEEK_DAYS_IN_ORDER.find(
    (weekDay) => !configuredWeekDays.includes(weekDay),
  );

  const addAvailabilityDay = (weekDay: WeekDayEnum) =>
    availabilityFieldArray.append({
      weekDay,
      timeIntervals: [{ from: 9, to: 10 }],
    });

  const removeAvailabilityDay = (index: number) =>
    availabilityFieldArray.remove(index);

  const addInterval = (dayIndex: number) => {
    const key = `availability.${dayIndex}.timeIntervals` as const;
    const current = (getValues(key) as { from: number; to: number }[]) || [];
    const next = [...current, { from: 9, to: 10 }];
    setValue(key, next, { shouldValidate: true, shouldDirty: true });
  };

  const removeInterval = (dayIndex: number, tiIndex: number) => {
    const key = `availability.${dayIndex}.timeIntervals` as const;
    const current = (getValues(key) as { from: number; to: number }[]) || [];
    const next = current.filter((_, i) => i !== tiIndex);
    setValue(key, next, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <form>
      <Col>
        <Row>
          <InputWithLabel label="Group name">
            <TextInput
              disabled={isEditing}
              errorMessage={errors.name?.message}
              {...register("name")}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label="Thinkific bundle ID (opzionale)">
            <TextInput
              disabled={isEditing}
              type="number"
              inputMode="numeric"
              errorMessage={errors.thinkificBundleId?.message}
              {...register("thinkificBundleId", { valueAsNumber: true })}
            />
          </InputWithLabel>
        </Row>

        <Row>
          <WeeklyAvailabilityCard
            availability={availability}
            hasErrors={!!errors.availability}
          >
            {availabilityFieldArray.fields.map((field, index) => (
              <div key={field.id} className="mb-4 rounded border p-3">
                <div className="mb-2 flex items-center gap-2">
                  <label className="text-sm">Giorno</label>
                  <select
                    className="rounded border px-2 py-1"
                    {...register(`availability.${index}.weekDay` as const)}
                    defaultValue={
                      (field as unknown as AvailabilityFormItem).weekDay
                    }
                  >
                    {WEEK_DAYS_IN_ORDER.map((d) => (
                      <option
                        key={d}
                        value={d}
                        disabled={
                          d !== availability[index]?.weekDay &&
                          configuredWeekDays.includes(d)
                        }
                      >
                        {weekDayLabels[d]}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="ml-auto rounded border px-2 py-1 text-red-600"
                    onClick={() => removeAvailabilityDay(index)}
                  >
                    Rimuovi giorno
                  </button>
                </div>
                <div className="flex flex-col gap-2">
                  {(availability[index]?.timeIntervals || []).map(
                    (ti, tiIndex) => (
                      <div key={tiIndex}>
                        <div className="flex items-center gap-2">
                          <label className="text-sm">Da</label>
                          <select
                            className="rounded border px-2 py-1"
                            defaultValue={ti.from}
                            {...register(
                              `availability.${index}.timeIntervals.${tiIndex}.from` as const,
                              { valueAsNumber: true },
                            )}
                          >
                            {HOURS.map((h) => (
                              <option key={h} value={h}>
                                {formatHour(h)}
                              </option>
                            ))}
                          </select>
                          <label className="text-sm">A</label>
                          <select
                            className="rounded border px-2 py-1"
                            defaultValue={ti.to}
                            {...register(
                              `availability.${index}.timeIntervals.${tiIndex}.to` as const,
                              { valueAsNumber: true },
                            )}
                          >
                            {HOURS.map((h) => (
                              <option key={h} value={h}>
                                {formatHour(h)}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="rounded border px-2 py-1 text-red-600"
                            onClick={() => removeInterval(index, tiIndex)}
                          >
                            Rimuovi
                          </button>
                        </div>
                        <FieldErrorMessage
                          message={
                            errors.availability?.[index]?.timeIntervals?.[
                              tiIndex
                            ]?.message
                          }
                        />
                      </div>
                    ),
                  )}
                  <FieldErrorMessage
                    message={
                      errors.availability?.[index]?.timeIntervals?.message
                    }
                  />
                  <button
                    type="button"
                    className="mt-2 w-max rounded border px-2 py-1"
                    onClick={() => addInterval(index)}
                  >
                    + Aggiungi intervallo
                  </button>
                </div>
              </div>
            ))}
            {firstFreeWeekDay ? (
              <button
                type="button"
                className="rounded border px-3 py-1"
                onClick={() => addAvailabilityDay(firstFreeWeekDay)}
              >
                + Aggiungi giorno
              </button>
            ) : null}
          </WeeklyAvailabilityCard>
        </Row>

        <Row>
          <div className="w-full space-y-3 rounded border p-3">
            <div className="font-semibold">Live onboarding</div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...register("onboardingLiveRequired")} />
              Gli utenti creati in questo gruppo devono partecipare a una live
              onboarding prima di usare la piattaforma
            </label>
            <p className="text-xs text-slate-500">
              La modifica vale per i nuovi utenti: gli utenti già presenti non
              vengono bloccati, salvo quelli segnati come &quot;da fare&quot;.
            </p>
            {onboardingLiveRequired ? (
              <InputWithLabel label="Giorni in avanti in cui lo studente vede le live prenotabili">
                <TextInput
                  type="number"
                  inputMode="numeric"
                  min={1}
                  errorMessage={errors.onboardingLiveBookingWindowDays?.message}
                  {...register("onboardingLiveBookingWindowDays", {
                    valueAsNumber: true,
                  })}
                />
              </InputWithLabel>
            ) : null}
          </div>
        </Row>

        <Row>
          <div className={"flex w-full justify-end"}>
            <PrimaryButton
              loading={loading}
              onClick={handleSubmit(onSubmit)}
              label={"Save"}
            />
          </div>
        </Row>
      </Col>
    </form>
  );
}
